// src/components/AdvertisementInline.jsx
import React, { useEffect, useMemo, useRef } from "react";
import { useAPI } from "../context/ApiContext";

const DEFAULT_FAVICON = "https://www.google.com/s2/favicons?sz=64&domain=example.com";

function pickBannerSizeFromHtml(html) {
  if (!html) return null;
  const re = /<img[^>]*\bwidth="(\d+)"[^>]*\bheight="(\d+)"[^>]*>/gi;
  let m, best = null;
  while ((m = re.exec(html))) {
    const w = parseInt(m[1], 10), h = parseInt(m[2], 10);
    if (!w || !h) continue;
    if (w <= 10 || h <= 10) continue; // 1x1等トラッカー除外
    const area = w * h;
    if (!best || area > best.area) best = { w, h, area };
  }
  return best ? { w: best.w, h: best.h } : { w: 300, h: 250 };
}

const AdvertisementInline = ({ ad }) => {
  const { API_BASE, incrementAdEvent } = useAPI();
  const impressedRef = useRef(false);

  // ---- Hooksはここで全部呼ぶ ----
  const host = useMemo(() => {
    try { return ad?.url ? new URL(ad.url).hostname : ""; } catch { return ""; }
  }, [ad?.url]);

  const favicon = host
    ? `https://www.google.com/s2/favicons?sz=64&domain=${encodeURIComponent(host)}`
    : DEFAULT_FAVICON;

  // 通常画像広告用のURL（affiliateでも計算してOK：コスト軽微）
  const imgUrl = useMemo(() => {
    if (!ad?.img) return "";
    try { return new URL(ad.img).href; }
    catch { return `${API_BASE}${ad.img.startsWith("/") ? "" : "/"}${ad.img}`; }
  }, [ad?.img, API_BASE]);

  // affiliate_html から実寸を拾う（affiliateでなくても呼んでOK）
  const bannerSize = useMemo(() => pickBannerSizeFromHtml(ad?.affiliate_html), [ad?.affiliate_html]);
  const bannerW = bannerSize?.w ?? 300;
  const bannerH = bannerSize?.h ?? 250;

  // Impression 課金は表示時に一度だけカウント
  useEffect(() => {
    if (!ad?.signedId) return;
    if (ad?.billing === 0 /* IMP */ && !impressedRef.current) {
      impressedRef.current = true;
      incrementAdEvent(ad.signedId, "imp").catch(() => {});
    }
  }, [ad?.signedId, ad?.billing, incrementAdEvent]);

  const onClick = async () => {
    if (!ad?.url) return;
    try {
      if (ad?.signedId && ad?.billing === 1 /* CLK */) {
        await incrementAdEvent(ad.signedId, "clk");
      }
    } finally {
      window.open(ad.url, "_blank", "noopener,noreferrer");
    }
  };

  // 追加: affiliate用のsrcDocをラップ（純粋HTML + <base target="_blank"> だけ）
const affiliateDoc = useMemo(() => {
  const raw = ad?.affiliate_html || "";
  return `<!doctype html><html><head><base target="_blank"></head><body>${raw}</body></html>`;
}, [ad?.affiliate_html]);

  // ---------- アフィリエイトHTML（純粋表示） ----------
  if (ad?.is_affiliate && ad?.affiliate_html) {
    return (
      <div className="card shadow-xss rounded-xxl border-light-md mb-2">
        <div className="card-body ps-3 pe-3 pt-3 pb-0 d-flex align-items-center">
          <figure className="avatar me-3" style={{ marginBottom: 0 }}>
            <img src={favicon} alt="sponsor" className="shadow-sm rounded-circle w40" />
          </figure>
          <h4 className="fw-700 text-grey-900 font-xssss mt-1">
            Sponsored
            <span className="d-block font-xssss fw-500 mt-1 lh-3 text-grey-500">
              {host || ad?.affiliate_source || "Affiliate"}
            </span>
          </h4>
        </div>

        {/* 実寸を上限に、横幅が狭い画面では縮小だけ許可（拡大しない） */}
        <div className="px-0">
        <div
          style={{
            width: `min(100%, ${bannerW}px)`,
            margin: "8px auto 0",
            position: "relative",
            aspectRatio: `${bannerW} / ${bannerH}`,
          }}
        >
          <iframe
            title={`affiliate-${ad.id}`}
            srcDoc={affiliateDoc}  // ← ここを ad.affiliate_html から置き換え
            sandbox="allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-forms allow-same-origin"
            style={{
              border: 0,
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              background: "transparent",
            }}
          />
        </div>
      </div>

        {ad?.content ? (
          <div className="card-body pt-2 pb-3 ps-3 pe-3">
            <p className="fw-600 text-grey-700 lh-26 font-xssss mb-0" style={{ whiteSpace: "pre-line" }}>
              {ad.content}
            </p>
          </div>
        ) : (
          <div style={{ height: 8 }} />
        )}
      </div>
    );
  }

  // ---------- 通常（画像/テキスト）広告 ----------
  return (
    <div className="card shadow-xss rounded-xxl border-light-md mb-2">
      <div className="card-body ps-3 pe-3 pt-3 pb-0 d-flex align-items-center">
        <figure className="avatar me-3" style={{ marginBottom: 0 }}>
          <img src={favicon} alt="sponsor" className="shadow-sm rounded-circle w40" />
        </figure>
        <h4 className="fw-700 text-grey-900 font-xssss mt-1">
          Sponsored
          <span className="d-block font-xssss fw-500 mt-1 lh-3 text-grey-500">{host || "Advertisement"}</span>
        </h4>
      </div>

      {ad?.content ? (
        <div className="card-body p-0 ps-3 pe-3 pt-2">
          <p className="fw-600 text-grey-700 lh-26 font-xssss mb-2" style={{ whiteSpace: "pre-line" }}>
            {ad.content}
          </p>
        </div>
      ) : null}

      {imgUrl ? (
        <div className="px-0">
          <img
            src={imgUrl}
            alt="ad"
            className="img-fluid w-100 pointer"
            onClick={onClick}
            style={{ display: "block", border: 0 }}
          />
        </div>
      ) : null}

      {ad?.url ? (
        <div className="card-body d-flex p-0 ps-3 pe-3 pt-2 pb-3 align-items-center">
          <button type="button" className="btn btn-sm bg-current text-white font-xssss fw-700" onClick={onClick}>
            詳細を見る
          </button>
          <span className="ms-2 font-xssss text-grey-600">{host}</span>
        </div>
      ) : (
        <div style={{ height: 12 }} />
      )}
    </div>
  );
};

export default AdvertisementInline;
